import React, { Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Routes from './routes/Routes';
import ReduxSuspense from './components/ReduxSuspense/ReduxSuspense';
import SeoContainer from './components/SeoContainer/SeoContainer';
import OneTrust from './components/OneTrust/OneTrust';

import { Matomo } from './components/Tracking/Matomo';
import ScrollToTop from './components/ScrollToTop';
import Theme from './components/themes';
import FloatingSidebar from './components/FloatingSidebar/FloatingSidebar';
import ToTop from './components/FloatingSidebar/ToTop';
import { sessionIdSelector } from '../modules/selectors/auth';
import { setNewSessionId } from '../modules/actions/auth/sessionId';
import HeadMeta from './components/Head/HeadMeta';
import HeaderSelector from './components/Header/HeaderSelector';
import { NoSSR } from '../helpers/SSRHelper';
import { useLocation } from 'react-router-dom';
import Anchor from './components/form-elements/Anchor';
import { SeoProvider, useSeoContext } from './context/SeoContext';
import { markenStandortSelector } from '../modules/selectors/standort/markenStandort';
import Image from './components/Image/Image';
import FooterSelector from './components/Footer/FooterSelector';
import ApsLocationTiles from './components/apsLocationTiles/ApsLocationTiles';
import APSRoutes from './routes/APSRoutes';
import LazyOnVisible from './components/LazyOnVisible/LazyOnVisible';
import RedirectCheck from '../helpers/getRiderectsHelper';
import { fetchRedirectsIfNeeded } from '../modules/actions/redirects';

const Footer = React.lazy(() => import('./components/Footer/Footer'));

function LayoutContent({ markenStandortResponse, seoMetaDataResponse }) {
    const dispatch = useDispatch();
    const sessionId = useSelector(sessionIdSelector);
    const location = useLocation();
    const isServiceNavExtern = location.pathname.includes("servicenavigatorExtern");
    const { handleOwnSeo } = useSeoContext();
    const markenStandort = useSelector(markenStandortSelector);
    
    RedirectCheck()
    useEffect(() => {
        if (!sessionId) dispatch(setNewSessionId());
    }, [dispatch, sessionId]);

    return (
        <>
            {markenStandort?.isTechno ?
                <>
                    <HeadMeta seoMetaDataResponse={seoMetaDataResponse} markenStandortResponse={markenStandortResponse} />
                    <Matomo />
                    <ReduxSuspense response={markenStandortResponse} fallback={null} error={null}>
                        <HeaderSelector />
                    </ReduxSuspense>
                    <ReduxSuspense response={markenStandortResponse} fallback={null} error={null}>
                        <main id='main_content' className='uk-container uk-flex uk-flex-middle uk-flex-column uk-padding uk-flex-stretch'>
                            <APSRoutes />
                        </main>
                        <footer>
                            <ReduxSuspense response={markenStandortResponse} fallback={null} error={null}>
                                <FooterSelector />
                            </ReduxSuspense>
                        </footer>

                        {/* Global Side Effect Components  */}
                        <Theme />
                        <NoSSR>
                            <ScrollToTop />
                            <OneTrust />
                        </NoSSR>
                        <ToTop />

                    </ReduxSuspense>
                </>
                :
                <>
                    <Anchor href="#skip_header" className="skip-link" aria-label="Zum Hauptinhalt springen" tabIndex={0}>
                        Zum Hauptinhalt springen
                    </Anchor>
                    {!isServiceNavExtern &&
                        <>
                            <HeadMeta seoMetaDataResponse={seoMetaDataResponse} markenStandortResponse={markenStandortResponse} />
                            <Matomo />
                            <ReduxSuspense response={markenStandortResponse} fallback={null} error={null}>
                                <HeaderSelector />
                            </ReduxSuspense>
                            {/* <FloatingSidebar /> */}
                        </>
                    }
                    <ReduxSuspense response={markenStandortResponse}>
                        <main id='main_content'>
                            <Routes />
                            {!isServiceNavExtern && !handleOwnSeo &&
                                <SeoContainer />
                            }
                            <RedirectCheck />

                            {(!isServiceNavExtern && !markenStandort?.withoutFooter) && (
                                <LazyOnVisible rootMargin="800px 0px">
                                    <footer role="contentinfo">
                                        <Suspense fallback={null}>
                                            <Footer />
                                        </Suspense>
                                    </footer>
                                </LazyOnVisible>
                            )}

                            {/* Global Side Effect Components  */}
                            <Theme />
                            <NoSSR>
                                {!isServiceNavExtern && <ScrollToTop />}
                                <OneTrust />
                            </NoSSR>
                            <ToTop />
                        </main>
                    </ReduxSuspense>
                </>
            }

        </>
    );
}

function Layout(props) {
    return (
        <SeoProvider>
            <LayoutContent {...props} />
        </SeoProvider>
    );
}

export default Layout;
